import { AuthErrorHandlerService } from '@aize/authentication';
import { IconService } from '@aize/shuffle-ds';
import { useTheme } from '@amcharts/amcharts4/core';
import { Component, effect, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { ApplicationAuthService } from '@services/application-auth.service';
import { ClarityService } from '@services/clarity.service';
import { DataTransferService } from '@services/data-transfer.service';
import { ElementService } from '@services/element.service';
import { AmCharts4Theme } from '@shared/components/charts/amcharts4-theme';
import { setupAgTestIds } from 'ag-grid-community';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { ApplicationInsightsService } from '@elementz/telemetry';
import { ApplicationNames } from './core/shared/enums/applications.types';
import { AppStateService } from './core/shared/layout/app-state.service';
import { ShellComponent } from './core/shared/layout/shell/shell.component';
import { NotificationsSignalrService } from './core/shared/services/notifications-signalr.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-root',
  imports: [RouterModule, ShellComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly authErrorHandlerService = inject(AuthErrorHandlerService);
  private readonly appAuthService = inject(ApplicationAuthService);
  private readonly dataTransferService = inject(DataTransferService);
  private readonly elementService = inject(ElementService);
  private readonly browserTitle = inject(Title);
  private readonly matIconReg = inject(MatIconRegistry);
  private readonly clarityService = inject(ClarityService);
  private readonly appStateService = inject(AppStateService);
  private readonly notificationSignalr = inject(NotificationsSignalrService);
  private readonly ngZone = inject(NgZone);
  private readonly appInsightsService = inject(ApplicationInsightsService);
  private readonly iconService = inject(IconService);

  private readonly browserTitle$ = this.dataTransferService.getBrowserTitle();
  private readonly destroy$: Subject<void> = new Subject<void>();
  private readonly twinUser = toSignal(this.appAuthService.twinUser$, { initialValue: null });

  constructor() {
    effect(() => {
      const user = this.appStateService.user();
      if (user) {
        this.notificationSignalr.initialize();
      }
    });

    effect(() => {
      const currentUser = this.twinUser();

      if (!currentUser?.sub) {
        this.appInsightsService.clearUser();
        return;
      }

      this.appInsightsService.setUser(currentUser.sub, currentUser.currentTenant);
    });
  }

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.appInsightsService.init();
    });

    this.appStateService.logInUser();
    this.appStateService.setApplication(ApplicationNames.Elementz);
    this.clarityService.initializeClarity();
    this.appAuthService.init().subscribe();
    this.iconService.init();
    setupAgTestIds();

    this.authErrorHandlerService.errors$.subscribe(err => {
      console.error('Aize authentication library error handler: ', err);
    });
    this.browserTitle$.subscribe(data => {
      this.browserTitle.setTitle(data);
    });
    this.matIconReg.setDefaultFontSetClass('material-symbols-outlined');
    useTheme(AmCharts4Theme); // apply AmCharts4 theme globally

    this.dataTransferService
      .getElementId()
      .pipe(
        switchMap(elementId => this.elementService.getById(elementId)),
        takeUntil(this.destroy$)
      )
      .subscribe(element => {
        this.dataTransferService.setElement(element);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
